import { computed, type ComputedRef } from 'vue'
import { storeToRefs } from 'pinia'
import { useSessionStore } from '../stores/session'
import { useWikiPagesStore } from '../stores/wiki_pages'
import { useSourceActivityStore } from '../stores/source_activity'
import { useLintNotesStore } from '../stores/lint_notes'
import { useSynthesisSuggestionsStore } from '../stores/synthesis_suggestions'
import { useAgentStore } from '../stores/agent'
import { useAgentHealthStore } from '../stores/agent_health'
import { LOG_PAGE_PATH, type NavBadgeKey } from '../lib/navConfig'

// The live signals the navigation surfaces carry — the counts and
// unread dots the old header spread over buttons and a dropdown badge.
// Read straight from the stores `useAdminSubscriptions` keeps warm, so
// the rail, the tab bar and the Today page all agree on the numbers.

export type BadgeTone = 'attention' | 'factory' | 'alert' | 'neutral'

export interface NavBadge {
  // Visible text; empty for a plain dot.
  text: string
  tone: BadgeTone
  // Spoken suffix for the nav link's accessible name.
  label: string
}

export interface NavSignals {
  isAdmin: ComputedRef<boolean>
  reviewCount: ComputedRef<number>
  runningSessionCount: ComputedRef<number>
  logUnread: ComputedRef<boolean>
  addToWikiActive: ComputedRef<boolean>
  factoryProblemCount: ComputedRef<number>
  factoryUnreachable: ComputedRef<boolean>
  badgeFor: (key: NavBadgeKey | undefined) => NavBadge | null
}

export function useNavSignals(): NavSignals {
  const { currentUser } = storeToRefs(useSessionStore())
  const { pages } = storeToRefs(useWikiPagesStore())
  const { hasActive } = storeToRefs(useSourceActivityStore())
  const { openCount: lintOpenCount } = storeToRefs(useLintNotesStore())
  const { openCount: synthesisOpenCount } = storeToRefs(useSynthesisSuggestionsStore())
  const { sessions } = storeToRefs(useAgentStore())
  const { problemCount, unreachable } = storeToRefs(useAgentHealthStore())

  const isAdmin = computed(() => currentUser.value?.admin === true)
  const reviewCount = computed(() => lintOpenCount.value + synthesisOpenCount.value)
  const runningSessionCount = computed(() => sessions.value.filter((s) => s.running).length)
  const addToWikiActive = computed(() => hasActive.value)

  // Unread when the log page changed after the user last opened it. A
  // user who has never opened it sees the dot; unparseable dates don't.
  const logUnread = computed(() => {
    const logPage = pages.value.find((p) => p.path === LOG_PAGE_PATH)
    if (!logPage) return false
    const seenAt = currentUser.value?.last_seen_log_at
    if (!seenAt) return true
    const updated = new Date(logPage.updated_at).getTime()
    const seen = new Date(seenAt).getTime()
    if (Number.isNaN(updated) || Number.isNaN(seen)) return false
    return updated > seen
  })

  function badgeFor(key: NavBadgeKey | undefined): NavBadge | null {
    switch (key) {
      case 'review':
        return reviewCount.value > 0
          ? { text: String(reviewCount.value), tone: 'attention', label: `${reviewCount.value} open for review` }
          : null
      case 'sessions':
        return runningSessionCount.value > 0
          ? { text: String(runningSessionCount.value), tone: 'factory', label: `${runningSessionCount.value} running` }
          : null
      case 'log':
        return logUnread.value ? { text: '', tone: 'alert', label: 'new entries since your last visit' } : null
      case 'factory-health':
        if (unreachable.value) return { text: '!', tone: 'alert', label: 'agent status unreachable' }
        return problemCount.value > 0
          ? { text: String(problemCount.value), tone: 'alert', label: `${problemCount.value} repos need attention` }
          : null
      default:
        return null
    }
  }

  return {
    isAdmin,
    reviewCount,
    runningSessionCount,
    logUnread,
    addToWikiActive,
    factoryProblemCount: problemCount,
    factoryUnreachable: computed(() => unreachable.value),
    badgeFor,
  }
}
